import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Container from "react-bootstrap/Container";
import {
  HiServer,
  HiDatabase,
  HiLightningBolt,
  HiArrowRight,
  HiCheckCircle,
  HiClock,
  HiShieldCheck,
  HiCog,
  HiChip,
  HiGlobe,
  HiCube,
  HiSupport,
  HiLocationMarker,
  HiDesktopComputer,
  HiTemplate,
} from "react-icons/hi";
import AppNavbar from "../components/Navbar";
import AppFooter from "../components/AppFooter";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import { url } from "../utils/pubUrl";
import { getImageIcon } from "../utils/iconRegistry";
import { getAppMetadata, getAppQuickLinks } from "../utils/appHints";
import "../styles/global.css";
import "../styles/Home.css";

// Product Features with detailed specifications
const PRODUCT_FEATURES = [
  {
    id: "compute",
    title: "Cloud Compute & VPS",
    titleAccent: "Compute",
    accentColor: "blue",
    description: "Enterprise-grade virtual machines with guaranteed resources.",
    startingPrice: "₹499/mo",
    pillar: "Managed VPS",
    summary:
      "Spin up production droplets with hourly billing, integrated monitoring, and support-ready insights.",
    metrics: [
      { label: "Deploy time", value: "< 60 sec" },
      { label: "vCPU scale", value: "1 - 32" },
      { label: "Burst network", value: "10 Gbps" },
    ],
    icon: HiServer,
    specs: [
      { label: "Deploy Time", value: "< 60 seconds", icon: HiClock },
      { label: "CPU Types", value: "Shared, Dedicated, Premium", icon: HiChip },
      { label: "Storage", value: "NVMe SSD (up to 3TB)", icon: HiDatabase },
      { label: "Network", value: "Up to 10 Gbps", icon: HiGlobe },
    ],
    features: [
      "Automatic OS patching & security updates",
      "Private networking & VPC support",
      "Attach block volumes on demand",
      "Custom firewall rules included",
    ],
  },
  {
    id: "spaces",
    title: "Object Storage (Spaces)",
    titleAccent: "Storage",
    accentColor: "violet",
    description: "S3-compatible object storage with built-in CDN",
    startingPrice: "₹299/mo",
    pillar: "Global object store",
    summary:
      "Serve and archive data globally with lifecycle policies, edge caching, and pay-as-you-grow pricing.",
    metrics: [
      { label: "Egress cached", value: "CDN included" },
      { label: "Durability", value: "11×9s" },
      { label: "APIs", value: "S3 + CLI" },
    ],
    icon: HiDatabase,
    specs: [
      { label: "Storage", value: "Unlimited scalability", icon: HiCube },
      { label: "Bandwidth", value: "CDN-accelerated", icon: HiGlobe },
      {
        label: "Availability",
        value: "99.99% uptime SLA",
        icon: HiShieldCheck,
      },
      { label: "Access", value: "S3-compatible API", icon: HiCog },
    ],
    features: [
      "Automatic lifecycle management",
      "Versioning & access control (ACL)",
      "CORS & encryption at rest",
      "Global CDN distribution",
    ],
  },
  {
    id: "block-storage",
    title: "Block Storage Volumes",
    titleAccent: "Volumes",
    accentColor: "blue",
    description: "Scalable SSD volumes that attach to your compute instances",
    startingPrice: "₹199/mo",
    pillar: "Attachable volumes",
    summary:
      "Mount NVMe-backed volumes to any droplet with hot-swap, snapshots, and expansion without downtime.",
    metrics: [
      { label: "Size range", value: "10GB - 10TB" },
      { label: "Max IOPS", value: "7,500" },
      { label: "Snapshots", value: "Auto policies" },
    ],
    icon: HiCube,
    specs: [
      { label: "Size Range", value: "10GB - 10TB", icon: HiDatabase },
      {
        label: "Performance",
        value: "Up to 7,500 IOPS",
        icon: HiLightningBolt,
      },
      { label: "Snapshots", value: "Automatic backups", icon: HiShieldCheck },
      { label: "Attachment", value: "Hot-swap capable", icon: HiCog },
    ],
    features: [
      "Expand volumes without downtime",
      "Automated snapshot scheduling",
      "Point-in-time recovery",
      "Multi-attach support (read-only)",
    ],
  },
];

// Why Choose Cyberin - Key Benefits
const BENEFIT_COLUMNS = [
  {
    id: "billing",
    eyebrow: "Pay-as-you-go",
    title: "Metered daily or monthly billing",
    description:
      "Only pay for the hours you consume. Pause a VPS or managed stack and we refund the remaining period automatically.",
  },
  {
    id: "launch",
    eyebrow: "Launch fast",
    title: "No complex deployment hurdles",
    description:
      "Blueprint libraries and automation remove YAML sprawl. We provision, harden, and verify your stack so you can ship features.",
  },
  {
    id: "support",
    eyebrow: "Included expertise",
    title: "Dedicated Cyberin engineers",
    description:
      "Talk directly to the team operating your infrastructure. Architecture reviews, migrations, and incident response are part of the service.",
  },
  {
    id: "governance",
    eyebrow: "Managed compliance",
    title: "Backups, monitoring, and rollback",
    description:
      "We maintain backups, patching, and observability runbooks. Deploy confidently knowing every change has a recovery plan.",
  },
];

// Technical Specifications
const PLATFORM_SPECS = [
  {
    category: "Compute Performance",
    items: [
      { label: "CPU Options", value: "1-32 vCPU cores" },
      { label: "RAM Options", value: "512MB - 192GB" },
      { label: "Storage Type", value: "NVMe SSD" },
      { label: "Network", value: "Up to 10 Gbps" },
    ],
  },
  {
    category: "Availability & Reliability",
    items: [
      { label: "Uptime SLA", value: "99.95%" },
      { label: "Data Centers", value: "15+ global locations" },
      { label: "Monitoring", value: "Real-time metrics" },
      { label: "Backup Frequency", value: "Configurable (hourly-weekly)" },
    ],
  },
  {
    category: "Security & Compliance",
    items: [
      { label: "DDoS Protection", value: "Included" },
      { label: "SSL Certificates", value: "Free Let's Encrypt" },
      { label: "Firewall", value: "Cloud & instance-level" },
      { label: "Encryption", value: "At-rest & in-transit" },
    ],
  },
];

const specValue = (category, label) => {
  const section = PLATFORM_SPECS.find((item) => item.category === category);
  return section?.items.find((entry) => entry.label === label)?.value || "";
};

const PLATFORM_HIGHLIGHTS = [
  {
    id: "uptime",
    value: `${specValue("Availability & Reliability", "Uptime SLA")} uptime`,
    description:
      "SLA-backed availability with proactive failover and health checks.",
    footer: "Reliability engineering included",
  },
  {
    id: "regions",
    value: specValue("Availability & Reliability", "Data Centers"),
    description:
      "Deploy workloads closer to your end users for consistent latency.",
    footer: "Global regions with local compliance",
  },
  {
    id: "throughput",
    value: specValue("Compute Performance", "Network"),
    description:
      "Burstable networking and private interconnects tuned for scale.",
    footer: "Premium transfer on every plan",
  },
  {
    id: "backups",
    value: specValue("Availability & Reliability", "Backup Frequency"),
    description:
      "Automated snapshot policies with one-click rollback workflows.",
    footer: "Managed by Cyberin SREs",
  },
];

// Global Regions - Matching order page format
const GLOBAL_REGIONS = [
  { slug: "nyc1", name: "New York", country: "us" },
  { slug: "sfo2", name: "San Francisco", country: "us" },
  { slug: "tor1", name: "Toronto", country: "ca" },
  { slug: "lon1", name: "London", country: "gb" },
  { slug: "fra1", name: "Frankfurt", country: "de" },
  { slug: "ams3", name: "Amsterdam", country: "nl" },
  { slug: "sgp1", name: "Singapore", country: "sg" },
  { slug: "blr1", name: "Bangalore", country: "in" },
  { slug: "syd1", name: "Sydney", country: "au" },
];

const POPULAR_APP_SHOWCASE = [
  {
    key: "wordpress",
    headline: "Launch marketing sites in minutes",
    description:
      "Pre-configured caching, SSL-ready, and optimized for publishing workflows.",
  },
  {
    key: "cyberpanel",
    headline: "Modern control panel for agencies",
    description:
      "Manage LiteSpeed-powered hosting with backups, staging, and email tooling baked in.",
  },
  {
    key: "cpanel",
    headline: "Build container platforms instantly",
    description:
      "Ship microservices faster with Docker pre-installed and production-ready.",
  },
  {
    key: "plesk",
    headline: "Enterprise-grade hosting automation",
    description:
      "Deliver managed WordPress, security hardening, and developer tooling in one dashboard.",
  },
  {
    key: "nodejs",
    headline: "Scale JavaScript backends effortlessly",
    description:
      "Node.js runtime and npm arrive pre-configured so your developers can focus on product.",
  },
];

const APPHINTS_NODE_POSITIONS = [
  { top: "18%", left: "25%" },
  { top: "10%", left: "50%" },
  { top: "18%", left: "75%" },
  { top: "38%", left: "12.5%" },
  { top: "38%", left: "87.5%" },
];

// OS Icon Component - matches OrderImageSection pattern
const OSIcon = ({ distribution }) => {
  const [state, setState] = useState({
    type: "icon",
    Icon: null,
    src: null,
    label: "",
  });

  useEffect(() => {
    if (!distribution) return;
    let mounted = true;
    (async () => {
      const baseSlug = (
        distribution.distribution ||
        distribution.slug?.split("-")[0] ||
        distribution.name ||
        ""
      )
        .toString()
        .toLowerCase();
      const res = await getImageIcon({
        slug: baseSlug,
        name: distribution?.name || baseSlug,
      });
      if (mounted) setState(res);
    })();
    return () => {
      mounted = false;
    };
  }, [distribution]);

  if (state.type === "img" && state.src) {
    return <img src={state.src} alt={state.label} className="os-icon-image" />;
  }
  const I = state.Icon;
  return I ? (
    <I className="os-icon-fallback" />
  ) : (
    <HiDesktopComputer className="os-icon-fallback" />
  );
};

const buildImageGroups = (distributions = []) => {
  const familyOf = (img) => {
    const base = (img.distribution || img.name || img.slug || "")
      .toString()
      .toLowerCase();
    if (base.includes("ubuntu")) return "Ubuntu";
    if (base.includes("debian")) return "Debian";
    if (base.includes("centos")) return "CentOS";
    if (base.includes("rocky")) return "Rocky Linux";
    if (base.includes("alma")) return "AlmaLinux";
    if (base.includes("fedora")) return "Fedora";
    if (base.includes("opensuse")) return "openSUSE";
    if (base.includes("freebsd")) return "FreeBSD";
    if (base.includes("arch")) return "Arch Linux";
    return img.distribution || "Other";
  };

  const groups = {};
  distributions.forEach((img) => {
    const family = familyOf(img);
    if (!groups[family]) groups[family] = [];
    groups[family].push(img);
  });

  Object.keys(groups).forEach((key) => {
    groups[key].sort((a, b) => {
      const an = a.name || "";
      const bn = b.name || "";
      return bn.localeCompare(an, undefined, { numeric: true });
    });
  });

  return groups;
};

const Home = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [imageGroups, setImageGroups] = useState({});
  const [applications, setApplications] = useState([]);
  const [selectedOS, setSelectedOS] = useState("");
  const [selectedOSVersion, setSelectedOSVersion] = useState("");
  const [selectedApp, setSelectedApp] = useState("");
  const [appSearch, setAppSearch] = useState("");
  const [appVisible, setAppVisible] = useState(8);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        setLoadingOptions(true);
        const [distRes, appsRes] = await Promise.all([
          api.get("/resources/distributions"),
          api.get("/resources/applications"),
        ]);
        if (!isMounted) {
          return;
        }
        const distData = Array.isArray(distRes.data) ? distRes.data : [];
        const appsData = Array.isArray(appsRes.data) ? appsRes.data : [];
        const grouped = buildImageGroups(distData);
        setImageGroups(grouped);
        const families = Object.keys(grouped);
        if (families.length > 0) {
          setSelectedOS((prev) => (prev && grouped[prev] ? prev : families[0]));
        }
        setApplications(appsData);
        if (appsData.length > 0) {
          setSelectedApp((prev) =>
            prev && appsData.some((app) => app.slug === prev)
              ? prev
              : appsData[0]?.slug || ""
          );
        }
      } catch (error) {
        console.error("Failed to load marketplace data", error);
      } finally {
        if (isMounted) {
          setLoadingOptions(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const families = Object.keys(imageGroups);
    if (!families.length) return;
    setSelectedOS((prev) => (prev && imageGroups[prev] ? prev : families[0]));
  }, [imageGroups]);

  useEffect(() => {
    if (!selectedOS || !imageGroups[selectedOS]?.length) return;
    const versions = imageGroups[selectedOS];
    setSelectedOSVersion((prev) =>
      prev && versions.some((img) => (img.slug || img.id) === prev)
        ? prev
        : versions[0]?.slug || versions[0]?.id || ""
    );
  }, [selectedOS, imageGroups]);

  const heroMetrics = useMemo(
    () => [
      {
        id: "deploy",
        value: "< 60s",
        label: "Average Deploy Time",
        description: "From order to live instance",
        icon: HiClock,
      },
      {
        id: "uptime",
        value: "99.95%",
        label: "Uptime SLA",
        description: "Backed by enterprise infrastructure",
        icon: HiShieldCheck,
      },
      {
        id: "support",
        value: "< 15min",
        label: "Response Time",
        description: "24/7 expert engineering support",
        icon: HiSupport,
      },
    ],
    []
  );

  const osFamilies = useMemo(() => Object.keys(imageGroups), [imageGroups]);

  const selectedFamilyImages = useMemo(
    () => imageGroups[selectedOS] || [],
    [imageGroups, selectedOS]
  );

  const filteredApps = useMemo(() => {
    const query = appSearch.trim().toLowerCase();
    if (!query) return applications;
    return applications.filter(({ name, slug, description, category }) => {
      const tokens = [name, slug, description, category].filter(Boolean);
      return tokens.some((token) => token.toLowerCase().includes(query));
    });
  }, [applications, appSearch]);

  const visibleApps = useMemo(
    () => filteredApps.slice(0, appVisible),
    [filteredApps, appVisible]
  );

  const handleOSSelect = (family) => {
    setSelectedOS(family);
    const versions = imageGroups[family] || [];
    setSelectedOSVersion(versions[0]?.slug || versions[0]?.id || "");
  };

  const handleOSVersionChange = (version) => {
    setSelectedOSVersion(version);
  };

  const handleApplicationSelect = (appSlug) => {
    setSelectedApp(appSlug);
  };

  const popularAppCards = useMemo(
    () =>
      POPULAR_APP_SHOWCASE.map(({ key, headline, description }) => {
        const metadata = getAppMetadata({ slug: key });
        if (!metadata) return null;
        const links = getAppQuickLinks({ slug: key });
        const primaryLink =
          links.find((link) => link.primary) || links[0] || null;
        const secondaryLinks = primaryLink
          ? links.filter((link) => link !== primaryLink)
          : links;
        return {
          key,
          headline,
          description,
          metadata,
          primaryLink,
          secondaryLinks,
        };
      }).filter(Boolean),
    []
  );

  const primaryCtaLabel = user ? "Go to Dashboard" : "Get Started";
  const primaryCtaAction = () => {
    if (user) {
      navigate("/dashboard");
      return;
    }
    navigate("/order");
  };

  const secondaryCtaAction = () => navigate("/spaces/order");

  return (
    <div className="home-page">
      <AppNavbar />

      {/* Hero Section with Grid Background */}
      <div className="home-hero-wrapper">
        <div className="home-hero-grid-bg"></div>
        <Container className="main-container">
          <section className="home-hero">
            <div className="home-hero-content">
              <div className="home-hero-badge">
                <HiLightningBolt /> Simplified Cloud Computes
              </div>
              <h1 className="home-hero-title">
                Managed Cloud Infrastructure
                <br />
                Built for <span className="text-blue">Developers</span>
              </h1>
              <p className="home-hero-subtitle">
                Deploy and scale droplets, object storage, and attachable
                volumes with enterprise-grade management. From single VPS builds
                to storage-backed workloads—all fully managed by our{" "}
                <span className="text-violet-subtitle">
                  expert infrastructure team
                </span>
                .
              </p>

              <div className="home-hero-actions">
                <button
                  className="btn-primary btn-cta home-cta-primary"
                  onClick={primaryCtaAction}
                >
                  {primaryCtaLabel}
                  <HiArrowRight />
                </button>
                <button
                  className="btn-secondary btn-cta home-cta-primary"
                  onClick={secondaryCtaAction}
                >
                  Block Storage
                </button>
              </div>

              <div className="home-hero-stats">
                {heroMetrics.map(
                  ({ id, value, label, description, icon: Icon }) => (
                    <div key={id} className="home-stat-item">
                      <div className="home-stat-icon">
                        <Icon />
                      </div>
                      <div className="home-stat-content">
                        <div className="home-stat-value">{value}</div>
                        <div className="home-stat-label">{label}</div>
                        <div className="home-stat-desc">{description}</div>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </section>
        </Container>
      </div>

      {/* Product Features Section */}

      <section className="home-section">
        <div className="home-hero-grid-bg-3"></div>
        <Container className="dashboard-container">
          <div className="home-section-header">
            <span className="home-eyebrow">Products & Specifications</span>
            <h2 className="home-section-title">
              Everything you need to build and{" "}
              <span className="text-blue">scale</span>
            </h2>
            <p className="home-section-desc">
              Our droplets, Spaces object storage, and block storage volumes are
              fully managed, monitored, and optimized for{" "}
              <span className="desc-highlight">performance</span>.
            </p>
          </div>

          <div className="home-products-grid">
            {PRODUCT_FEATURES.map(
              ({
                id,
                title,
                titleAccent,
                accentColor,
                description,
                startingPrice,
                pillar,
                summary,
                metrics,
                icon: Icon,
                specs,
                features,
              }) => (
                <div
                  key={id}
                  className={`home-product-card accent-${accentColor}`}
                >
                  <div className="home-product-header">
                    <div className="home-product-label-row">
                      <span className="home-product-pill">{pillar}</span>
                    </div>
                    <div className="home-product-title-group">
                      {(() => {
                        const hasAccent =
                          titleAccent && title.includes(titleAccent);
                        const parts = hasAccent
                          ? title.split(titleAccent)
                          : [title];
                        return (
                          <h3 className="home-product-title">
                            {parts[0]}
                            {hasAccent && (
                              <>
                                <span className={`text-${accentColor}`}>
                                  {titleAccent}
                                </span>
                                {parts.slice(1).join(titleAccent)}
                              </>
                            )}
                          </h3>
                        );
                      })()}
                      <p className="home-product-desc">{description}</p>
                      <p className="home-product-summary">{summary}</p>
                    </div>
                  </div>

                  <div className="home-product-body">
                    <div className="home-product-metrics">
                      {metrics.map(({ label, value }) => (
                        <div key={label} className="home-product-metric">
                          <span className="home-product-metric-value">
                            {value}
                          </span>
                          <span className="home-product-metric-label">
                            {label}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="home-product-specs">
                      <div className="home-spec-label">Key specifications</div>
                      <div className="home-product-specs-grid">
                        {specs.map((spec) => (
                          <div key={spec.label} className="home-spec-item">
                            <div className="home-spec-icon">
                              <spec.icon />
                            </div>
                            <div className="home-spec-content">
                              <div className="home-spec-name">{spec.label}</div>
                              <div className="home-spec-value">
                                {spec.value}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="home-product-features">
                      <div className="home-spec-label">
                        Included capabilities
                      </div>
                      <ul className="home-feature-list">
                        {features.map((feature) => (
                          <li key={feature}>
                            <HiCheckCircle className="home-feature-check" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </Container>
      </section>
      <section className="home-section home-regions-section">
        <Container>
          <div className="home-section-header">
            <span className="home-eyebrow">
              <HiLocationMarker className="eyebrow-icon" />
              Global Infrastructure
            </span>
            <h2 className="home-section-title">
              Deploy closer to your <span className="text-blue">users</span>
            </h2>
            <p className="home-section-desc">
              Choose from 15+ data center locations worldwide for optimal
              performance and compliance.
            </p>
          </div>

          <div className="home-regions-grid">
            {GLOBAL_REGIONS.map(({ slug, name, country }) => (
              <div key={slug} className="home-region-card">
                <div className="home-region-flag">
                  <img
                    src={url(`icons/flags/${country}.png`)}
                    alt={name}
                    className="region-flag-img"
                  />
                </div>
                <div className="home-region-name">{name}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>
      {/* Operating Systems Section */}

      <section className="home-section home-os-section">
        <Container>
          <div className="home-section-header">
            <span className="home-eyebrow">
              <HiDesktopComputer className="eyebrow-icon" />
              Operating Systems
            </span>
            <h2 className="home-section-title">
              Choose your preferred <span className="text-violet">OS</span>
            </h2>
            <p className="home-section-desc">
              Pre-configured images for all major Linux distributions with
              automatic security updates.
            </p>
          </div>

          <div className="home-os-panel">
            {loadingOptions && !osFamilies.length ? (
              <div className="home-panel-loading">
                Loading operating system images…
              </div>
            ) : (
              <>
                <div className="os-selection">
                  {osFamilies.map((family) => {
                    const familyImages = imageGroups[family] || [];
                    const representative = familyImages[0];
                    return (
                      <div
                        key={family}
                        className={`os-card ${
                          selectedOS === family ? "selected" : ""
                        }`}
                        onClick={() => handleOSSelect(family)}
                      >
                        <div className="os-icon">
                          <OSIcon distribution={representative} />
                        </div>
                        <div className="os-name">{family}</div>
                      </div>
                    );
                  })}
                </div>

                {[...selectedFamilyImages].reverse().length > 0 ? (
                  <div className="version-selection">
                    <label className="version-label">Version</label>
                    <div className="version-dropdown">
                      <select
                        className="version-select"
                        value={selectedOSVersion}
                        onChange={(event) =>
                          handleOSVersionChange(event.target.value)
                        }
                      >
                        {selectedFamilyImages.map((image) => (
                          <option
                            key={image.slug || image.id}
                            value={image.slug || image.id}
                          >
                            {image.name || image.slug}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="home-panel-loading subtle">
                    We are refreshing image versions. Check back shortly.
                  </div>
                )}
              </>
            )}
          </div>
        </Container>
      </section>
      {/* One-Click Applications Section */}

      <section className="home-section home-apps-section">
        <Container>
          <div className="home-section-header">
            <span className="home-eyebrow">
              <HiTemplate className="eyebrow-icon" />
              One-Click Applications
            </span>
            <h2 className="home-section-title">
              Deploy popular apps in <span className="text-blue">seconds</span>
            </h2>
            <p className="home-section-desc">
              Pre-configured droplet stacks that complement your workloads. From
              CMS deployments to control panels, get started instantly.
            </p>
          </div>

          <div className="home-app-panel">
            <div className="app-search-row">
              <input
                type="text"
                className="form-input"
                placeholder="Search applications (e.g., WordPress)"
                value={appSearch}
                disabled={loadingOptions && !applications.length}
                onChange={(event) => {
                  setAppSearch(event.target.value);
                  setAppVisible(8);
                }}
              />
            </div>
            {loadingOptions && !applications.length ? (
              <div className="home-panel-loading">
                Loading marketplace catalog…
              </div>
            ) : visibleApps.length > 0 ? (
              <>
                <div className="marketplace-grid">
                  {visibleApps.map((app) => {
                    const slug = app.slug || app.id;
                    const name = app.name || slug;
                    return (
                      <div
                        key={slug}
                        className={`marketplace-card ${
                          selectedApp === slug ? "selected" : ""
                        }`}
                        onClick={() => handleApplicationSelect(slug)}
                      >
                        <div className="marketplace-icon">
                          <HiCube />
                        </div>
                        <div className="marketplace-name">{name}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="home-panel-loading subtle">
                No applications match your search.
              </div>
            )}
          </div>
        </Container>
      </section>

      {/* Why Choose Section */}

      <section className="home-benefits-section">
        <div className="home-hero-grid-bg-2"></div>
        <Container>
          <div className="home-benefits-shell">
            <div className="home-benefits-heading">
              <span className="home-eyebrow">Why choose Cyberin</span>
              <h2>
                Focus on your <span className="text-violet">product</span>. We
                manage the cloud.
              </h2>
              <p>
                Predictable pay-as-you-go billing, automated refunds for unused
                hours, and human engineers on-call. Cyberin keeps your droplets,
                storage, and volumes production ready while your team ships
                features.
              </p>
            </div>
            <div className="home-benefits-grid">
              {BENEFIT_COLUMNS.map(({ id, eyebrow, title, description }) => (
                <div key={id} className="home-benefit-column">
                  <div className="home-benefit-header">
                    <span className="home-benefit-eyebrow">{eyebrow}</span>
                    <span className="home-benefit-arrow">
                      <HiArrowRight />
                    </span>
                  </div>
                  <h3 className="home-benefit-title">{title}</h3>
                  <p className="home-benefit-desc">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </Container>
      </section>
      {/* Global Regions Section */}

      {/* Platform Specifications */}
      <section className="home-specs-section">
        <Container>
          <div className="home-specs-grid">
            <div className="home-specs-hero">
              <span className="home-eyebrow">Platform Specifications</span>
              <h2>
                Enable fast teams with{" "}
                <span className="text-blue">managed velocity</span>
              </h2>
              <p>
                Cloud infrastructure focused on core building blocks. We manage
                droplets, Spaces, and block volumes so your engineers stay
                focused on customer features.
              </p>
              <button
                type="button"
                className="home-specs-cta"
                onClick={() => navigate("/order")}
              >
                Explore architecture
                <HiArrowRight />
              </button>
            </div>

            {PLATFORM_HIGHLIGHTS.map(({ id, value, description, footer }) => (
              <div key={id} className="home-specs-card">
                <div className="home-specs-value">{value}</div>
                <p className="home-specs-description">{description}</p>
                <div className="home-specs-footer">{footer}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {popularAppCards.length > 0 && (
        <section className="home-section home-apphints-section">
          <Container>
            <div className="home-section-header">
              <span className="home-eyebrow">
                <HiTemplate className="eyebrow-icon" />
                Popular One-Click Applications
              </span>
              <h2 className="home-section-title">
                Production-ready stacks{" "}
                <span className="text-blue">curated by Cyberin</span>
              </h2>
              <p className="home-section-desc">
                Visualize the droplet templates our engineers deploy the
                most—ready for guided migrations, security hardening, and scale.
              </p>
            </div>

            <div className="home-apphints-visual">
              <div className="apphints-arc arc-outer"></div>
              <div className="apphints-arc arc-middle"></div>
              <div className="apphints-arc arc-inner"></div>
              <div className="apphints-center">
                <img
                  src="/cyberin-logo-square.png"
                  alt="Cyberin - Logo Square"
                />
              </div>
              {popularAppCards.map(({ key, metadata }, index) => {
                if (!metadata) return null;
                const position =
                  APPHINTS_NODE_POSITIONS[
                    index % APPHINTS_NODE_POSITIONS.length
                  ];

                return (
                  <div
                    key={key}
                    className="apphints-node"
                    style={{
                      top: position.top,
                      left: position.left,
                      "--float-delay": `${index * 0.8}s`,
                    }}
                  >
                    <div
                      className={`apphints-icon-wrapper ${
                        metadata.customIcon ? "has-custom-icon" : ""
                      }`}
                    >
                      {metadata.customIcon ? (
                        <img
                          src={url(`icons/os/${metadata.customIcon}.png`)}
                          alt={metadata.name}
                          onError={(event) =>
                            event.currentTarget.parentElement.classList.remove(
                              "has-custom-icon"
                            )
                          }
                        />
                      ) : (
                        <HiTemplate />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Container>
        </section>
      )}
      {/* Final CTA Section */}

      <section className="home-cta-section">
        <Container>
          <div className="home-cta-content">
            <h2 className="home-cta-title">
              Ready to <span className="cta-highlight-blue">deploy</span>?
            </h2>
            <p className="home-cta-desc">
              Start building with a{" "}
              <span className="cta-text-bold">free account</span>. Speak to an
              expert for your <span className="cta-text-bold">Pro</span> or{" "}
              <span className="cta-text-bold">Enterprise</span> needs.
            </p>
            <div className="home-cta-actions">
              <button
                className="btn-primary btn-cta home-cta-primary"
                onClick={primaryCtaAction}
              >
                {loading
                  ? "Loading..."
                  : user
                  ? "Go to Dashboard"
                  : "Start Deploying"}
                <HiArrowRight />
              </button>
              <button
                className="btn-secondary btn-cta home-cta-primary"
                onClick={() =>
                  window.open(
                    "https://cyberin.in/#callbacktrackerwidget",
                    "_blank"
                  )
                }
              >
                Talk to an Expert
              </button>
            </div>
          </div>
        </Container>
      </section>

      <AppFooter />
    </div>
  );
};

export default Home;
